// The ☰ menu of the play view (plan §5.4). Opening it pauses the game. Every string that came from the manifest is
// rendered as text only.
import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { Manifest } from '../../shared/manifest.ts';
import { controlsFor, rebind, type Keymap } from '../lib/keyboard.ts';
import type { TouchMode } from './touch.tsx';
import type { AudioOut } from '../audio/engine.ts';
import { canCopy, checkImport, copyFile, downloadBytes, FILES_CAP, saveFileName, type GameFile } from '../lib/saves.ts';
import { formatBytes } from '../format.ts';

export function SoundSection({ audio }: { audio: AudioOut }) {
  const st = audio.state();
  return (
    <Section title="Sound" name="sound">
      <label class="check">
        <input type="checkbox" data-field="muted" checked={audio.prefs.muted} onChange={(e) => audio.setMuted((e.target as HTMLInputElement).checked)} />
        {' '}Mute
      </label>
      <label>Volume{' '}
        <input type="range" min={0} max={100} data-field="volume" value={Math.round(audio.prefs.volume * 100)}
          onInput={(e) => audio.setVolume(Number((e.target as HTMLInputElement).value) / 100)} />
      </label>
      <p class="muted small" data-field="audio-status">
        {st.state === 'off' ? 'Sound starts when you tap or press a key.' : `${st.kind}, ${st.state}${st.queuedMs !== undefined ? `, ${Math.round(st.queuedMs)} ms queued` : ''}`}
      </p>
    </Section>
  );
}

export function Section({ title, children, name }: { title: string; children: ComponentChildren; name: string }) {
  return (
    <section class="menu-section" data-section={name}>
      <h3>{title}</h3>
      {children}
    </section>
  );
}

export function TouchSection({ mode, onMode }: { mode: TouchMode; onMode: (m: TouchMode) => void }) {
  return (
    <Section title="Touch controls" name="touch">
      <div class="seg" role="radiogroup">
        {(['auto', 'on', 'off'] as const).map((m) => (
          <label key={m}>
            <input type="radio" name="touch-mode" value={m} data-field={`touch-${m}`} checked={mode === m} onChange={() => onMode(m)} />
            {' '}{m === 'auto' ? 'Auto' : m === 'on' ? 'On' : 'Off'}
          </label>
        ))}
      </div>
    </Section>
  );
}

export function keyName(code: string): string {
  const arrows: Record<string, string> = { ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→' };
  return arrows[code] ?? code.replace(/^Key/, '').replace(/^Digit/, '').replace(/^Numpad/, 'Num ');
}

export interface KeysProps {
  manifest: Manifest;
  map: Keymap;
  onMap: (m: Keymap) => void;
  onReset: () => void;
  /** Ask the player page to hand the next key press to `cb` instead of the game. */
  capture: (cb: ((code: string) => void) | null) => void;
}

export function KeysSection({ manifest, map, onMap, onReset, capture }: KeysProps) {
  const [listening, setListening] = useState<string | null>(null);
  const controls = controlsFor(manifest.platform, manifest.controls?.extra ?? []);
  useEffect(() => () => capture(null), []);
  const listen = (id: string) => {
    setListening(id);
    capture((code) => {
      capture(null);
      setListening(null);
      if (code !== 'Escape') onMap(rebind(map, id, code));
    });
  };
  const keysOf = (id: string) => Object.entries(map).filter(([, v]) => v === id).map(([k]) => keyName(k));
  return (
    <Section title="Keyboard" name="keys">
      <table class="keys">
        <tbody>
          {controls.map((c) => (
            <tr key={c.id} data-control={c.id}>
              <td>{c.label}</td>
              <td class="muted" data-field="keys">{keysOf(c.id).join(', ') || '—'}</td>
              <td>
                <button data-action="rebind" onClick={() => listen(c.id)}>
                  {listening === c.id ? 'Press a key… (Esc: cancel)' : 'Change'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button data-action="reset-keys" onClick={onReset}>Reset to defaults</button>
      <p class="muted small">Gamepads use the standard layout{manifest.platform === 'n64'
        ? ': left stick = stick, right stick = C buttons, LT = Z.' : ': east = A, south = B, shoulders = L/R.'}</p>
    </Section>
  );
}

export function OptionsSection({ manifest, values, onValue }: {
  manifest: Manifest; values: Record<number, number>; onValue: (id: number, v: number) => void;
}) {
  const opts = manifest.options ?? [];
  if (!opts.length) return null;
  return (
    <Section title="Game options" name="options">
      {opts.map((o) => {
        const v = values[o.id] ?? o.default ?? 0;
        if (o.type === 'bool') {
          return (
            <label key={o.id} class="check" data-option={o.id}>
              <input type="checkbox" checked={!!v} onChange={(e) => onValue(o.id, (e.target as HTMLInputElement).checked ? 1 : 0)} />
              {' '}{o.label}
            </label>
          );
        }
        if (o.type === 'enum') {
          return (
            <label key={o.id} data-option={o.id}>{o.label}{' '}
              <select value={String(v)} onChange={(e) => onValue(o.id, Number((e.target as HTMLSelectElement).value))}>
                {(o.choices ?? []).map((c) => <option key={c.value} value={String(c.value)}>{c.label}</option>)}
              </select>
            </label>
          );
        }
        return (
          <label key={o.id} data-option={o.id}>{o.label}{' '}
            <input type="number" value={v} min={o.min} max={o.max}
              onChange={(e) => {
                const n = Math.round(Number((e.target as HTMLInputElement).value));
                if (Number.isFinite(n)) onValue(o.id, Math.max(o.min ?? -2147483648, Math.min(o.max ?? 2147483647, n)));
              }} />
          </label>
        );
      })}
    </Section>
  );
}

export function Menu({ onClose, children }: { onClose: () => void; children: ComponentChildren }) {
  return (
    <div class="menu" role="dialog" aria-label="Menu" data-state="menu">
      <div class="menu-head">
        <strong>Menu</strong>
        <button data-action="close-menu" onClick={onClose}>Back to game</button>
      </div>
      {children}
    </div>
  );
}

export function SaveSection({ manifest, current, info, onImport }: {
  manifest: Manifest; current: () => Uint8Array | undefined; info: { at: number; error?: string } | null; onImport: (b: Uint8Array) => void;
}) {
  const [msg, setMsg] = useState('');
  if (!manifest.save || manifest.save.kind === 'none') return null;
  const name = saveFileName(manifest);
  const exportSave = () => {
    const b = current();
    if (!b) { setMsg('There is no save yet.'); return; }
    downloadBytes(name, 'application/octet-stream', b);
  };
  const importSave = async (f: File | undefined) => {
    if (!f) return;
    const bytes = new Uint8Array(await f.arrayBuffer());
    const err = checkImport(manifest, bytes);
    if (err) { setMsg(err); return; }
    onImport(bytes);
  };
  return (
    <Section title="Save" name="save">
      <p class="muted small" data-field="save-status">
        {info ? (info.error ? `The last save could not be stored: ${info.error}` : `Saved on this device at ${new Date(info.at).toLocaleTimeString()}.`)
          : current() ? 'A save from earlier is loaded.' : 'No save yet.'}
        {' '}Native {manifest.save.kind} format, works in other emulators.
      </p>
      <button data-action="export-save" onClick={exportSave}>Export save ({name})</button>{' '}
      <label class="button-like">
        Import save…
        <input type="file" data-field="import-save" accept=".sav,.srm,.eep,.sra,.fla,.mpk,.bin"
          onChange={(e) => { const i = e.target as HTMLInputElement; importSave(i.files?.[0]); i.value = ''; }} />
      </label>
      {msg && <p class="field-error" role="alert">{msg}</p>}
    </Section>
  );
}

type FileInput = NonNullable<Manifest['fileInputs']>[number];

/** The fileInputs entry (v1.1) a saved file can be opened with, by its extension. */
export function inputFor(inputs: readonly FileInput[] | undefined, name: string): FileInput | undefined {
  const ext = name.toLowerCase().match(/\.[a-z0-9]{1,8}$/)?.[0];
  return ext ? inputs?.find((i) => i.accept.includes(ext)) : undefined;
}

/** v1.1: files the player can hand to the game (manifest fileInputs). */
export function FileInputsSection({ inputs, onPick }: { inputs?: readonly FileInput[]; onPick: (input: FileInput, file: File) => void }) {
  if (!inputs?.length) return null;
  return (
    <Section title="Open a file in the game" name="file-inputs">
      {inputs.map((i) => (
        <label class="button-like" key={i.id} data-input={i.id}>
          {i.label}…
          <input type="file" data-field={`file-input-${i.id}`} accept={i.accept.join(',')}
            onChange={(e) => { const el = e.target as HTMLInputElement; const f = el.files?.[0]; el.value = ''; if (f) onPick(i, f); }} />
        </label>
      ))}
    </Section>
  );
}

export function FilesSection({ files, stored, note, onRemove, inputs, onOpen }: {
  files: GameFile[]; stored: boolean; note?: string; onRemove: (id: number) => void;
  inputs?: readonly FileInput[]; onOpen?: (input: FileInput, f: GameFile) => void;
}) {
  const [copied, setCopied] = useState<number | null>(null);
  if (!files.length) return null;
  return (
    <Section title="Saved files" name="files">
      <p class="muted small">Files the game made for you (photos, recordings…). {stored
        ? `They are kept on this device (up to ${FILES_CAP.bytes / 1048576} MB for this game; the oldest go first) until you remove them.`
        : 'They stay until you close the game.'}</p>
      {note && <p class="muted small" role="status" data-field="files-note">{note}</p>}
      <ul class="files">
        {files.map((f) => (
          <li key={f.id} data-file={f.name}>
            <span>{f.name}</span> <span class="muted small">{formatBytes(f.bytes.length)} · {new Date(f.at).toLocaleString()}</span>{' '}
            <button data-action="file-save" onClick={() => downloadBytes(f.name, f.mime, f.bytes)}>Save</button>{' '}
            {canCopy(f.mime) && (
              <button data-action="file-copy" onClick={async () => { if (await copyFile(f)) { setCopied(f.id); setTimeout(() => setCopied(null), 1500); } }}>
                {copied === f.id ? 'Copied' : 'Copy'}
              </button>
            )}{' '}
            {onOpen && inputFor(inputs, f.name) && (
              <><button data-action="file-open" onClick={() => onOpen(inputFor(inputs, f.name)!, f)}>Open in game</button>{' '}</>
            )}
            <button data-action="file-remove" onClick={() => onRemove(f.id)} aria-label={`Delete ${f.name}`}>Delete</button>
          </li>
        ))}
      </ul>
    </Section>
  );
}
